<template>
    <!-- Tree SVG connector for grouped child rows. Mirrors Current's
         benefits-table-row.vue: an absolute container sits in the
         ml-12 area on the left (and 16 extra px outside) and hosts
         the L-curve so subsequent columns stay aligned with the
         parent row. -->
    <div
        v-if="indent"
        class="absolute bg-white dark:bg-slate-800 w-16 -left-16 top-0 bottom-0 h-full group-hover:bg-slate-50 dark:group-hover:bg-slate-700 pointer-events-none"
    >
        <svg
            v-if="index === 0"
            width="12"
            height="70"
            viewBox="0 0 12 70"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="absolute -top-2 left-12"
        >
            <path
                d="M1.5 5V50Q1.5 63 12 63"
                stroke="#CBD5E1"
                stroke-width="1"
                stroke-linecap="round"
            />
        </svg>
        <svg
            v-else
            width="12"
            height="110"
            viewBox="0 0 12 110"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="absolute -top-12 left-12"
        >
            <path
                d="M1.5 0V85Q1.5 98 12 98"
                stroke="#CBD5E1"
                stroke-width="1"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <!-- Benefit -->
    <div
        class="flex items-center min-w-0 px-6"
        :class="[colWidth('benefit'), { 'opacity-50': locked }]"
    >
        <BenefitIcon
            :category="row.item.benefit?.category"
            :title="row.item.benefit?.benefit_title"
            :description="row.item.benefit?.benefit_description"
            svg-class="w-6 h-6 text-slate-600 dark:text-slate-300"
            class="mr-4"
        />
        <div class="min-w-0 flex-1">
            <div
                class="font-medium text-sm leading-5 text-left text-slate-800 dark:text-slate-100 line-clamp-1"
            >
                {{ row.name || 'Unknown Benefit' }}
            </div>
            <div
                v-if="showCardInfo"
                class="font-normal text-slate-800 dark:text-slate-100 text-sm flex items-center gap-2 mt-1"
            >
                <div class="shrink-0">
                    <ImageLoader>
                        <template #image>
                            <img
                                v-if="
                                    row.item.card?.image?.path &&
                                    row.item.card?.image?.file_name
                                "
                                :src="`${cdnUrl}/${row.item.card.image.path}/_small/${row.item.card.image.file_name}`"
                                :alt="row.card_name"
                                class="w-8 h-5 rounded-sm"
                            />
                        </template>
                        <template #error>
                            <Icon icon="credit-card" class="w-8 h-5" />
                        </template>
                    </ImageLoader>
                </div>
                <div class="flex items-center min-w-0">
                    <span class="truncate mr-2">
                        {{ row.card_name || 'Unknown Card' }}
                    </span>
                    <span
                        v-if="showCardNumbers && row.card_last_4"
                        class="text-slate-500 dark:text-slate-400 text-xs shrink-0 whitespace-nowrap"
                    >
                        ({{ row.card_last_4 }})
                    </span>
                </div>
            </div>
            <div
                v-else-if="row.value_up_to !== '—'"
                class="text-slate-500 dark:text-slate-400 text-sm py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg max-w-fit font-medium mt-1"
            >
                Up to {{ row.value_up_to }}
            </div>
        </div>
    </div>

    <!-- User (only when wallet has multiple users) -->
    <div
        v-if="hasUserColumn"
        class="px-6 flex items-center"
        :class="colWidth('user')"
    >
        <div
            v-if="!indent && row.item.user?.name"
            class="flex items-center gap-2 min-w-0"
        >
            <div
                class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700 shrink-0"
            >
                <Icon
                    :icon="row.item.user.is_business ? 'business' : 'user'"
                    class="w-3 h-3 text-slate-500 dark:text-slate-400"
                />
            </div>
            <span class="text-slate-800 dark:text-slate-100 text-sm truncate">
                {{ row.item.user.name }}
            </span>
        </div>
    </div>

    <!-- Value up to -->
    <div
        class="px-6 flex items-center"
        :class="[colWidth('value_up_to'), { 'opacity-50': locked }]"
    >
        <div
            v-if="!indent && row.value_up_to !== '—'"
            class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg"
        >
            <p class="text-slate-500 dark:text-slate-400 font-medium text-sm whitespace-nowrap">
                {{ row.value_up_to }}
            </p>
        </div>
        <span v-else-if="!indent" class="text-slate-400 text-sm">—</span>
    </div>

    <!-- Used selected period -->
    <div
        class="px-6 gap-2 flex flex-col"
        :class="[colWidth('used'), { '-ml-12': indent }]"
    >
        <button
            v-if="locked"
            type="button"
            class="flex items-center gap-1 text-orange text-sm font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
            @click.stop="openUpgrade"
        >
            <Icon icon="lock" class="w-4 h-4" />
            <span>Upgrade</span>
        </button>
        <template v-else>
            <div
                class="w-full max-w-[120px] bg-orange-200 rounded-full h-1 relative"
            >
                <div
                    class="bg-10x-orange h-1 rounded-full transition-all"
                    :style="{ width: clamp(row.utilization) + '%' }"
                ></div>
                <div
                    v-if="Number(row.utilization) >= 100"
                    class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                >
                    <Icon
                        icon="check"
                        svgClass="w-2 h-2 text-white"
                        class="text-white"
                    />
                </div>
            </div>
            <div class="text-sm text-slate-500 dark:text-slate-400 font-normal whitespace-nowrap">
                {{ formatMoney(row.used) }} of {{ formatMoney(row.total) }}
            </div>
        </template>
    </div>

    <!-- Unused -->
    <div class="px-6 flex items-center" :class="colWidth('unused')">
        <span v-if="locked" class="text-slate-400 text-sm">—</span>
        <span v-else class="text-slate-700 dark:text-slate-300 text-sm whitespace-nowrap">
            {{ formatMoney(row.unused) }}
        </span>
    </div>

    <!-- Utilization -->
    <div class="px-6 flex items-center" :class="colWidth('utilization')">
        <span v-if="locked" class="text-slate-400 text-sm">—</span>
        <span v-else class="text-slate-800 dark:text-slate-100 text-sm font-medium">
            {{ row.utilization }}%
        </span>
    </div>

    <!-- Updates -->
    <div
        class="px-6 flex flex-col gap-1 justify-center"
        :class="[colWidth('frequency'), locked && 'opacity-50']"
    >
        <div
            v-if="indent && row.expiration_time"
            class="text-slate-800 dark:text-slate-100 font-normal text-sm whitespace-nowrap"
        >
            {{ row.expiration_time }}
        </div>
        <span
            class="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400 text-sm whitespace-nowrap"
        >
            <Icon icon="arrows-repeat" class="w-4 h-4" />
            {{ row.frequency }}
        </span>
    </div>

    <!-- Action -->
    <div class="w-32 shrink-0 flex justify-end">
        <div class="flex items-center px-6 gap-2">
            <AddUsagePopover
                v-if="indent && !locked"
                :benefit-item="row.item"
                @click.stop
            />
            <button
                v-if="!locked"
                type="button"
                class="btn-icon !rounded-full"
                aria-label="View benefit details"
                @click.stop="$emit('open', row.item)"
            >
                <Icon icon="chevron-right" svgClass="w-3 h-3 m-1" />
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useCardBenefitsStore } from '@/store/card-benefits';
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import AddUsagePopover from '@/components/card-benefits/add-usage-popover.vue';

const props = defineProps({
    row: { type: Object, required: true },
    columns: { type: Array, required: true },
    showCardNumbers: { type: Boolean, default: true },
    cdnUrl: { type: String, default: '' },
    showCardInfo: { type: Boolean, default: true },
    indent: { type: Boolean, default: false },
    index: { type: Number, default: 0 },
    locked: { type: Boolean, default: false },
});

defineEmits(['open']);

const store = useCardBenefitsStore();
const openUpgrade = () => store.setShowUpgradeModal(true);

const colWidth = (key) => {
    const col = props.columns.find((c) => c.key === key);
    return col ? col.width : '';
};

const hasUserColumn = computed(() =>
    props.columns.some((c) => c.key === 'user')
);

const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    return '$' + Math.round(Number(value)).toLocaleString('en-US');
};
</script>
